import React from 'react';
import {
  convertClientDateToServerDate,
  formatDateToDayShortWithYear,
  getAge,
} from 'src/utils/dateUtils';
import { pluralizeYears } from 'src/utils/pluralizeUtils';
import styled, { css } from 'styled-components';
import { useTranslation } from '@lt/localization';
import type { GENDER } from '@lt/api/dist/types/tourists';
import type { TouristsState } from 'src/store/tourists/slice';
import {
  TEST_ID_MOBILE_SUMMARY_TOURIST_INFO_CHANGE_BTN,
  TEST_ID_MOBILE_SUMMARY_TOURIST_INFO_NAME,
} from './constants';

type Props = {
  tourist: TouristsState;
  onClick: () => void;
  testId: string;
};

const TouristInfo = ({ tourist, onClick, testId }: Props) => {
  const {
    name,
    surname,
    birthday,
    passport_number,
    passport_expires_on,
    gender,
  } = tourist;

  const birthdayInClientFormat = birthday
    ? convertClientDateToServerDate(birthday)
    : null;

  const birthdayFormatted = birthdayInClientFormat
    ? formatDateToDayShortWithYear(birthdayInClientFormat)
    : null;

  const age = getAge(birthdayInClientFormat);

  const { t } = useTranslation();

  return (
    <Wrapper data-testid={testId}>
      <Name data-testid={TEST_ID_MOBILE_SUMMARY_TOURIST_INFO_NAME}>
        {surname} {name} <GenderIndicator $gender={gender} />
      </Name>
      <DocInfo>
        {age && birthdayFormatted && (
          <div>
            {`${age} ${pluralizeYears(age)}`} · {birthdayFormatted}
          </div>
        )}
        <div>
          {passport_number}{' '}
          {passport_expires_on &&
            `· ${new Date(passport_expires_on).toLocaleDateString(
              'ru-RU',
              {},
            )}`}
        </div>
      </DocInfo>
      <ChangeTouristDataBtn
        data-testid={TEST_ID_MOBILE_SUMMARY_TOURIST_INFO_CHANGE_BTN}
        onClick={onClick}
      >
        {t('checkout.summary.tourists.buttonText')}
      </ChangeTouristDataBtn>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  font-size: 14px;

  > * + * {
    margin-top: 4px;
  }
`;

const Name = styled.div`
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.05em;

  display: flex;
  align-items: baseline;
`;

const GenderIndicator = styled.div<{ $gender?: GENDER }>`
  width: 8px;
  height: 8px;
  border-radius: 100%;
  margin-left: 4px;

  ${(props) =>
    props.$gender === 'M' &&
    css`
      background-color: #007aff;
    `}

  ${(props) =>
    props.$gender === 'F' &&
    css`
      background-color: #e567b2;
    `}
`;

const DocInfo = styled.div`
  color: #4d4d4d;
`;

const ChangeTouristDataBtn = styled.div`
  color: #007aff;
  cursor: pointer;

  &:active {
    color: #179fe3;
  }
`;

export default TouristInfo;
